
import { AggregatedSessionV2 } from "@global/types/session";
import BookmarkAddIcon from "@mui/icons-material/BookmarkAdd";
import FilterListIcon from "@mui/icons-material/FilterList";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { SingleSegmentSelector } from "../../components/segment-selector";
import { useDataStore } from "../../context";
import { useFilters } from "../../context/filters-context";
import {
  DEFAULT_SEGMENTS,
  FILTER_VALUE_TYPES,
  Segment,
  SegmentFilter,
  SegmentFilterType,
  SegmentOperator,
} from "../../types/analytics";
import { filterSessionsBySegment } from "../overview/analytics";
import { columns, columnToSegmentFilterType } from "./columns";
import { FilterPopover } from "./FilterPopover";
import { SessionDetailDialog } from "./SessionDetailDialog";
import { Column, Order } from "./types";

/** Get the default operator for a filter type when creating segments */
function getDefaultOperatorForType(
  filterType: SegmentFilterType
): SegmentOperator {
  const valueType = FILTER_VALUE_TYPES[filterType];
  switch (valueType) {
    case "number":
      return "equals";
    case "boolean":
      return "equals";
    case "date":
      return "after";
    default:
      return "contains";
  }
}

export const SessionsPage: React.FC = () => {
  const {
    sessions,
    sessionsLoading: loading,
    sessionsError: error,
    deleteSession,
  } = useDataStore();

  const { saveSegment, savedSegments } = useFilters();

  // Local state for segment filter
  const [activeSegment, setActiveSegment] = useState<Segment>(
    savedSegments[0] || DEFAULT_SEGMENTS[0]
  );

  // Table state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [orderBy, setOrderBy] = useState<Column["id"]>("sessionStart");
  const [order, setOrder] = useState<Order>("desc");

  // Filter state
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(
    {}
  );
  const [globalFilter, setGlobalFilter] = useState("");
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const [activeFilterColumn, setActiveFilterColumn] = useState<Column | null>(
    null
  );
  const [selectedSession, setSelectedSession] =
    useState<AggregatedSessionV2 | null>(null);

  // Sorting
  const handleRequestSort = (property: Column["id"]) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // Filtering
  const handleFilterClick = (
    event: React.MouseEvent<HTMLElement>,
    column: Column
  ) => {
    setFilterAnchorEl(event.currentTarget);
    setActiveFilterColumn(column);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
    setActiveFilterColumn(null);
  };

  const handleColumnFilterChange = (value: string) => {
    if (activeFilterColumn) {
      setColumnFilters((prev) => ({
        ...prev,
        [activeFilterColumn.id]: value,
      }));
    }
  };

  // Pagination
  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    // First apply segment filter - spread to avoid mutating original array during sort
    let data = [...filterSessionsBySegment(sessions, activeSegment)];

    // Apply global filter
    if (globalFilter) {
      const lowerFilter = globalFilter.toLowerCase();
      data = data.filter((session) => {
        return columns.some((col) => {
          const value = col.getValue?.(session);
          if (value === null || value === undefined) return false;
          return String(value).toLowerCase().includes(lowerFilter);
        });
      });
    }

    // Apply column filters
    Object.entries(columnFilters).forEach(([colId, filterValue]) => {
      if (!filterValue) return;
      const column = columns.find((c) => c.id === colId);
      if (!column?.getValue) return;

      const lowerFilter = filterValue.toLowerCase();
      data = data.filter((session) => {
        const value = column.getValue!(session);
        if (value === null || value === undefined) return false;
        return String(value).toLowerCase().includes(lowerFilter);
      });
    });

    // Sort
    const sortColumn = columns.find((c) => c.id === orderBy);
    if (sortColumn?.getValue) {
      data.sort((a, b) => {
        const aVal = sortColumn.getValue!(a);
        const bVal = sortColumn.getValue!(b);

        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;

        let comparison = 0;
        if (typeof aVal === "string" && typeof bVal === "string") {
          comparison = aVal.localeCompare(bVal);
        } else if (typeof aVal === "boolean" && typeof bVal === "boolean") {
          comparison = aVal === bVal ? 0 : aVal ? -1 : 1;
        } else {
          comparison = (aVal as number) - (bVal as number);
        }

        return order === "asc" ? comparison : -comparison;
      });
    }

    return data;
  }, [sessions, globalFilter, columnFilters, orderBy, order, activeSegment]);

  // Paginated data
  const paginatedData = useMemo(() => {
    return filteredAndSortedData.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [filteredAndSortedData, page, rowsPerPage]);

  // Active filter count
  const activeFilterCount = useMemo(() => {
    return Object.values(columnFilters).filter(Boolean).length;
  }, [columnFilters]);

  // Check if current filters can create a segment
  const segmentableFilters = useMemo(() => {
    return Object.entries(columnFilters)
      .filter(([colId, value]) => {
        if (!value) return false;
        const filterType = columnToSegmentFilterType[colId];
        return filterType !== null && filterType !== undefined;
      })
      .map(([colId, value]) => ({
        colId,
        filterType: columnToSegmentFilterType[colId]!,
        value,
        column: columns.find((c) => c.id === colId),
      }));
  }, [columnFilters]);

  const canCreateSegment = segmentableFilters.length > 0;

  // Create segment from current filters
  const handleCreateSegmentFromFilters = () => {
    if (!canCreateSegment) return;

    const filters: SegmentFilter[] = segmentableFilters.map(
      ({ filterType, value }, index) => ({
        id: `filter-${Date.now()}-${index}`,
        type: filterType,
        operator: getDefaultOperatorForType(filterType),
        value: value,
      })
    );

    // Generate a name from the filters
    const filterDescriptions = segmentableFilters.map(
      ({ column, value }) => `${column?.label}: "${value}"`
    );
    const segmentName = `Sessions: ${filterDescriptions.join(", ")}`;

    const newSegment: Segment = {
      id: `segment-${Date.now()}`,
      name: segmentName.slice(0, 50) + (segmentName.length > 50 ? "..." : ""),
      description: `Auto-created from session filters: ${filterDescriptions.join(
        "; "
      )}`,
      filters,
      createdAt: new Date().toISOString(),
    };

    saveSegment(newSegment);
    setActiveSegment(newSegment);
  };

  // Create segment from a single column filter (called from FilterPopover)
  const handleCreateSegmentFromSingleFilter = (
    columnId: string,
    filterType: SegmentFilterType,
    value: string
  ) => {
    const column = columns.find((c) => c.id === columnId);
    const operator = getDefaultOperatorForType(filterType);
    const filter: SegmentFilter = {
      id: `filter-${Date.now()}`,
      type: filterType,
      operator,
      value: value,
    };

    const operatorLabel =
      operator === "contains"
        ? "contains"
        : operator === "equals"
        ? "="
        : operator === "after"
        ? "after"
        : operator;
    const segmentName = `${
      column?.label || columnId
    }: ${operatorLabel} "${value}"`;

    const newSegment: Segment = {
      id: `segment-${Date.now()}`,
      name: segmentName.slice(0, 50) + (segmentName.length > 50 ? "..." : ""),
      description: `Sessions where ${
        column?.label || columnId
      } ${operatorLabel} "${value}"`,
      filters: [filter],
      createdAt: new Date().toISOString(),
    };

    saveSegment(newSegment);
    setActiveSegment(newSegment);
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header with global filter and segment selector */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack spacing={2}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Typography variant="h6">
              Raw Sessions
              {!loading && (
                <Typography
                  component="span"
                  variant="body2"
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  ({filteredAndSortedData.length.toLocaleString()} sessions
                  {filteredAndSortedData.length !== sessions.length &&
                    ` of ${sessions.length.toLocaleString()}`}
                  )
                </Typography>
              )}
            </Typography>

            <Stack direction="row" spacing={2} alignItems="center">
              {activeFilterCount > 0 && (
                <>
                  <Chip
                    label={`${activeFilterCount} filter${
                      activeFilterCount > 1 ? "s" : ""
                    } active`}
                    size="small"
                    onDelete={() => setColumnFilters({})}
                  />
                  {canCreateSegment && (
                    <Tooltip title="Save current filters as a reusable segment">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<BookmarkAddIcon />}
                        onClick={handleCreateSegmentFromFilters}
                      >
                        Save as Segment
                      </Button>
                    </Tooltip>
                  )}
                </>
              )}
              <TextField
                size="small"
                placeholder="Search all columns..."
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
                sx={{ minWidth: 250 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">🔍</InputAdornment>
                  ),
                }}
              />
            </Stack>
          </Stack>

          {/* Segment Selector */}
          <SingleSegmentSelector
            selectedSegment={activeSegment}
            onSegmentChange={setActiveSegment}
          />
        </Stack>
      </Paper>

      {/* Table */}
      <Paper>
        <TableContainer sx={{ maxHeight: "calc(100vh - 350px)" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{ minWidth: column.minWidth }}
                    sx={{ fontWeight: 600, bgcolor: "background.paper" }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={0.5}
                      justifyContent={
                        column.align === "right" ? "flex-end" : "flex-start"
                      }
                    >
                      {column.sortable ? (
                        <TableSortLabel
                          active={orderBy === column.id}
                          direction={orderBy === column.id ? order : "asc"}
                          onClick={() => handleRequestSort(column.id)}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : (
                        <span>{column.label}</span>
                      )}
                      {column.filterable && (
                        <IconButton
                          size="small"
                          onClick={(e) => handleFilterClick(e, column)}
                          sx={{
                            opacity: columnFilters[column.id] ? 1 : 0.3,
                            "&:hover": { opacity: 1 },
                          }}
                        >
                          <FilterListIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                // Loading skeleton
                Array.from({ length: rowsPerPage }).map((_, index) => (
                  <TableRow key={index}>
                    {columns.map((column) => (
                      <TableCell key={column.id}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ py: 4 }}
                    >
                      No sessions found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((session) => (
                  <TableRow
                    hover
                    key={session.sessionId}
                    onClick={() => setSelectedSession(session)}
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                      cursor: "pointer",
                    }}
                  >
                    {columns.map((column) => {
                      const value =
                        column.id in session
                          ? session[column.id as keyof AggregatedSessionV2]
                          : null;
                      return (
                        <TableCell key={column.id} align={column.align}>
                          {column.format
                            ? column.format(value, session)
                            : String(value ?? "-")}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredAndSortedData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Filter Popover */}
      <FilterPopover
        anchorEl={filterAnchorEl}
        column={activeFilterColumn}
        filterValue={
          activeFilterColumn ? columnFilters[activeFilterColumn.id] || "" : ""
        }
        onFilterChange={handleColumnFilterChange}
        onClose={handleFilterClose}
        columnToFilterType={columnToSegmentFilterType}
        onCreateSegment={handleCreateSegmentFromSingleFilter}
      />

      {/* Session Detail Dialog */}
      <SessionDetailDialog
        session={selectedSession}
        onClose={() => setSelectedSession(null)}
        onDelete={deleteSession}
      />
    </Box>
  );
};
